/**
 * Phase 5C — Admin V5 Tutor Payouts Panel.
 * Reads /api/admin/tutor-payouts (returns masked rows for non-admin staff).
 * Lets admin / owner mark batches paid.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCcw, CheckCircle2, AlertCircle, Eye, Wallet, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const money = (cents) => `AUD ${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-AU'); } catch { return iso; } };

const AdminTutorPayoutsPanel = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_earned_cents: 0, pending_payout_cents: 0, paid_payout_cents: 0, winhouse_revenue_cents: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [canSeeFull, setCanSeeFull] = useState(false);
  const [viewer, setViewer] = useState('');
  const [methodModal, setMethodModal] = useState(null); // {tutor_email, method, loading}
  const [payModal, setPayModal] = useState(null);       // {tutor_email, total_cents, ...}

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await ax.get('/api/admin/tutor-payouts');
      setRows(r.data?.items || []);
      setTotals(r.data?.totals || { total_earned_cents: 0, pending_payout_cents: 0, paid_payout_cents: 0, winhouse_revenue_cents: 0 });
      setCanSeeFull(!!r.data?.can_see_full);
      setViewer(r.data?.viewer_role || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load tutor payouts');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openMethod = async (em) => {
    setMethodModal({ tutor_email: em, method: null, loading: true });
    try {
      const r = await ax.get(`/api/admin/tutor-payouts/${encodeURIComponent(em)}/method`);
      setMethodModal({ tutor_email: em, method: r.data?.method, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load payout details');
      setMethodModal(null);
    }
  };

  const startMarkPaid = (row) => {
    if ((row.pending_payout_cents || 0) <= 0) { toast.info('No pending earnings to mark paid'); return; }
    setPayModal({
      tutor_email: row.tutor_email,
      tutor_name: row.tutor_name,
      total_cents: row.pending_payout_cents,
      payout_date: new Date().toISOString().slice(0, 10),
      payout_method_used: row.payout_method_type || '',
      payout_reference: '',
      payout_note: '',
      busy: false,
    });
  };

  const requestPayoutMethod = async (tutor_email) => {
    try {
      const r = await ax.post(`/api/admin/v5/tutors/${encodeURIComponent(tutor_email)}/request-payout-method`, {});
      if (r.data?.skipped) toast.info('Payout method already on file');
      else if (r.data?.email_status === 'success') toast.success(`Payout request emailed to ${tutor_email}`);
      else toast.warning(`Notified in-portal · email: ${r.data?.email_status || 'n/a'}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send payout request');
    }
  };

  const confirmMarkPaid = async () => {
    if (!payModal) return;
    setPayModal((p) => ({ ...p, busy: true }));
    try {
      const r = await ax.post('/api/admin/tutor-payouts/mark-paid', {
        tutor_email: payModal.tutor_email,
        payout_date: new Date(payModal.payout_date).toISOString(),
        payout_method_used: payModal.payout_method_used || null,
        payout_reference: payModal.payout_reference || null,
        payout_note: payModal.payout_note || null,
      });
      toast.success(`Marked ${money(r.data.total_cents)} paid for ${payModal.tutor_email}`);
      setPayModal(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mark paid failed');
      setPayModal((p) => ({ ...p, busy: false }));
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  return (
    <Card data-testid="admin-tutor-payouts-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Wallet className="h-5 w-5" /> Tutor payouts</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Track payout details + mark earnings as paid. All amounts in AUD ({viewer === 'staff' || viewer === 'support' ? 'sensitive fields masked' : 'unmasked'}).
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={fetchAll} disabled={refreshing} data-testid="payouts-refresh">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {/* KPI cards (Phase 5C) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="tutor-payouts-kpis">
          <KpiCard tone="amber" label="Pending tutor payouts" value={money(totals.pending_payout_cents)} testid="kpi-pending-tutor-payouts" />
          <KpiCard tone="emerald" label="Paid tutor payouts" value={money(totals.paid_payout_cents)} testid="kpi-paid-tutor-payouts" />
          <KpiCard tone="sky" label="Total tutor earnings" value={money(totals.total_earned_cents)} testid="kpi-total-tutor-earnings" />
          <KpiCard tone="slate" label="Total Winhouse revenue" value={money(totals.winhouse_revenue_cents)} testid="kpi-winhouse-revenue" />
        </div>
        {rows.length === 0 ? (
          <div className="text-center text-sm text-gray-400 italic py-12">No tutors yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wide text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 pr-3">Tutor</th>
                  <th className="text-left py-2 pr-3">Method</th>
                  <th className="text-left py-2 pr-3">Total earned</th>
                  <th className="text-left py-2 pr-3">Pending</th>
                  <th className="text-left py-2 pr-3">Paid out</th>
                  <th className="text-left py-2 pr-3">Last paid</th>
                  <th className="text-left py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.tutor_email} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`payout-row-${r.tutor_email}`}>
                    <td className="py-2 pr-3">
                      <div className="font-semibold text-gray-900">{r.tutor_name}</div>
                      <div className="text-[10px] text-gray-500">{r.tutor_email}</div>
                    </td>
                    <td className="py-2 pr-3">
                      {r.payout_method_status === 'active' ? (
                        <Badge className="bg-emerald-100 text-emerald-800 gap-1"><CheckCircle2 className="h-3 w-3" /> {(r.payout_method_type || 'set').replace('_', ' ')}</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 gap-1"><AlertCircle className="h-3 w-3" /> Missing</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-bold">{money(r.total_earned_cents)}</td>
                    <td className="py-2 pr-3 text-amber-700 font-bold" data-testid={`pending-${r.tutor_email}`}>{money(r.pending_payout_cents)}</td>
                    <td className="py-2 pr-3 text-emerald-700">{money(r.paid_payout_cents)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.last_paid_out_at)}</td>
                    <td className="py-2 pr-3 flex gap-1.5 flex-wrap">
                      {canSeeFull && r.payout_method_status === 'active' && (
                        <Button size="sm" variant="outline" className="text-[10px] gap-1" onClick={() => openMethod(r.tutor_email)} data-testid={`view-method-${r.tutor_email}`}>
                          <Eye className="h-3 w-3" /> View
                        </Button>
                      )}
                      {canSeeFull && r.payout_method_status === 'missing' && (
                        <Button size="sm" variant="outline" className="text-[10px] gap-1 border-amber-400 text-amber-700 hover:bg-amber-50"
                          onClick={() => requestPayoutMethod(r.tutor_email)} data-testid={`request-payout-${r.tutor_email}`}>
                          <Mail className="h-3 w-3" /> Request
                        </Button>
                      )}
                      {canSeeFull && r.pending_payout_cents > 0 && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] gap-1" onClick={() => startMarkPaid(r)} data-testid={`mark-paid-${r.tutor_email}`}>
                          <CheckCircle2 className="h-3 w-3" /> Mark Paid
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* View Method Modal */}
      {methodModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setMethodModal(null)} data-testid="view-method-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-red-700" /> Payout details</div>
            {methodModal.loading ? (
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            ) : (
              <div className="space-y-2 text-xs">
                {Object.entries(methodModal.method || {}).map(([k, v]) => (
                  v && k !== '_id' && k !== 'id' ? (
                    <div key={k} className="flex justify-between border-b py-1.5">
                      <span className="text-gray-500 uppercase text-[10px] tracking-wider">{k.replace(/_/g, ' ')}</span>
                      <span className="font-mono text-gray-900">{String(v)}</span>
                    </div>
                  ) : null
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setMethodModal(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Paid Modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="mark-paid-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-sm font-bold text-gray-900 mb-1">Mark tutor paid</div>
            <div className="text-[11px] text-gray-500 mb-3">
              {payModal.tutor_name} ({payModal.tutor_email}) — pending <strong>{money(payModal.total_cents)}</strong>
            </div>
            <div className="space-y-3">
              <Field label="Payout date">
                <input type="date" value={payModal.payout_date} onChange={(e) => setPayModal({ ...payModal, payout_date: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm" data-testid="mp-date" />
              </Field>
              <Field label="Method used">
                <input type="text" value={payModal.payout_method_used} onChange={(e) => setPayModal({ ...payModal, payout_method_used: e.target.value })} placeholder="bank_transfer / mpesa / wise..." className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm" data-testid="mp-method" />
              </Field>
              <Field label="Reference / transaction ID">
                <input type="text" value={payModal.payout_reference} onChange={(e) => setPayModal({ ...payModal, payout_reference: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm" data-testid="mp-ref" />
              </Field>
              <Field label="Internal note (optional)">
                <textarea value={payModal.payout_note} onChange={(e) => setPayModal({ ...payModal, payout_note: e.target.value })} className="w-full h-16 px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="mp-note" />
              </Field>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setPayModal(null)}>Cancel</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={confirmMarkPaid} disabled={payModal.busy} data-testid="mp-confirm">
                {payModal.busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Confirm paid
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</label>
    {children}
  </div>
);

const KpiCard = ({ tone, label, value, testid }) => {
  const tones = {
    amber: 'from-amber-50 to-amber-100 text-amber-900 border-amber-200',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-900 border-emerald-200',
    sky: 'from-sky-50 to-sky-100 text-sky-900 border-sky-200',
    slate: 'from-slate-50 to-slate-100 text-slate-900 border-slate-200',
  };
  return (
    <div className={`bg-gradient-to-br ${tones[tone] || tones.slate} rounded-xl p-4 border`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-lg font-bold mt-1.5">{value}</div>
    </div>
  );
};

export default AdminTutorPayoutsPanel;
